///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { isVideoRenderMode } from "@/earthbrowser/utils/renderMode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { animation, easing } from "@stovokor/math/animation"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { HurricaneSymbolNode } from "./HurricaneSymbolNode"
import { symbolHalfThickness } from "./HurricaneSymbolGeometry"

// Dome radius scales with intensity: depressions ~12 km, cat 5 ~32 km.
// Storms are few and newsworthy, so they get a generous size floor.
const cBaseRadius = 12
const cRadiusPerCategory = 4

// Screen-space size floors (radians): hurricanes read as a clear dot from
// orbit (matching breaking quakes), weaker systems slightly subtler
const cMinAngularHurricane = 0.0045
const cMinAngularStorm = 0.003

// Face-on solidity of the dome
const cStormFill = 0.5

// The spinning cyclone symbol matches the synoptic H/L marker footprint:
// those letters are ~126 km tall (font size 12 × scale 15), so the symbol
// gets a 62 km tip radius (~124 km across). It sits flat in the tangent
// plane, lifted so its underside clears the marker's terrain altitude.
const cSymbolRadiusKm = 62
// Hide the symbol once it would be smaller than ~5px at 1080p; the dome
// carries the visual from orbit (same gating idea as fire perimeters)
const cSymbolMinAngular = 0.004
// Spin rate rises gently with intensity
const cSpinBaseRadPerSec = 0.5
const cSpinPerCategory = 0.12
// Symbol fade while the storm is focused, so it doesn't obscure structure
const cFocusFade = 0.85
// Fade the symbol out as the camera dives toward the surface, where a
// 120 km flat symbol would fill the whole view
const cNearFadeStartKm = 300
const cNearFadeEndKm = 120
// With the cloud overlay on, the real eye is the most interesting thing on
// screen from regional range in, and the symbol would sit right on top of
// it, so it hands off to the clouds much earlier. The dome stays as the
// orbit-scale marker and hit target
const cCloudNearFadeStartKm = 3000
const cCloudNearFadeEndKm = 1500

export interface StormMarkerStyle {
	category: number
	windKt: number | null
	/** Positive latitude spins counterclockwise, negative clockwise */
	latitude: number
}

/** Dome radius in km for a storm's category */
export function stormMarkerRadius(category: number): number {
	return cBaseRadius + Math.max(0, category) * cRadiusPerCategory
}

/**
 * Composite tropical cyclone marker:
 * - An ember dome tinted by the standard intensity ramp, pulsing while at
 *   hurricane strength; it carries the visual from orbit
 * - The classic newscast cyclone symbol as a spinning extruded red mesh in
 *   the tangent plane, sized to match the synoptic H/L pressure markers and
 *   rotating in the correct direction for the hemisphere
 *
 * The storm's cloud structure itself comes from the IR cloud overlay
 * (overlay_clouds.glsl), not from the marker.
 */
export class StormMarkerNode extends NodeBase {
	private readonly dome: EmberDomeNode
	private readonly spinner = new NodeBase()
	private readonly context: StovokorContext
	private symbol: HurricaneSymbolNode
	private northern: boolean
	private focusAnim?: animation
	private focusBlend = 0
	private focused = false

	private style: StormMarkerStyle
	private spinAngle = 0
	private lastFrameMs = 0
	/** 1 at cruising distance, 0 when the camera dives toward the surface */
	private nearFade = 1

	public radius: number
	/** Current minimum apparent size (radians); used for hit test inflation too */
	public minAngularSize = 0

	constructor(context: StovokorContext, style: StormMarkerStyle) {
		super()
		this.context = context
		this.style = style
		this.radius = stormMarkerRadius(style.category)
		this.northern = style.latitude >= 0

		this.dome = new EmberDomeNode(context)
		this.dome.fillAlpha = cStormFill
		this.addNode(this.dome)

		this.addNode(this.spinner)
		this.symbol = this.buildSymbol()
		this.spinner.addNode(this.symbol)

		this.updateStyle(style)
	}

	/** The symbol is built in the XY plane, so lay it flat in the local
	 *  tangent plane (spun about local up by the spinner) and lift it so the
	 *  extrusion's underside clears the terrain. Matrix chains apply to the
	 *  vertex left-to-right, and in this tangent frame -Y points away from
	 *  the surface (same convention the synoptic H/L text offsets use).
	 *  The +half_pi pitch turns the geometry's front (+Z) face toward the
	 *  sky; pitching the other way would show its back face from above and
	 *  mirror the chirality baked into the geometry. */
	private buildSymbol(): HurricaneSymbolNode {
		const symbol = new HurricaneSymbolNode(this.context, this.northern)
		symbol.transform = matrix.scale(cSymbolRadiusKm)
			.rotateBy(vec3.unitX, trig.half_pi)
			.translateBy(0, -(cSymbolRadiusKm * symbolHalfThickness + 1), 0)
		return symbol
	}

	/** Recompute size, color and animation state. Called on creation and on
	 *  every dataset poll, and with interpolated values while clock scrubbing. */
	public updateStyle(style: StormMarkerStyle): void {
		this.style = style
		this.radius = stormMarkerRadius(style.category)
		this.dome.transform = matrix.scale(this.radius)

		this.dome.color = DesignColors.stormCategoryColor(style.category, style.windKt)
		// Pulse strictly means hurricane-strength winds
		this.dome.pulse = style.category >= 1 ? 1 : 0

		this.minAngularSize = style.category >= 1 ? cMinAngularHurricane : cMinAngularStorm
		this.dome.minAngularSize = this.minAngularSize

		// Rebuild the symbol with mirrored chirality if the storm center
		// crossed the equator (arms always trail the rotation)
		const northern = style.latitude >= 0
		if (northern !== this.northern) {
			this.northern = northern
			this.spinner.removeNode(this.symbol)
			this.symbol.destroy()
			this.symbol = this.buildSymbol()
			this.spinner.addNode(this.symbol)
		}
		this.applyBlends()
	}

	/** Fold the focus fade and the near fade into opacities. The near fade
	 *  clears the symbol away when the camera closes in, where a 120 km
	 *  flat symbol would fill the whole view. */
	private applyBlends(): void {
		this.symbol.setOpacity((1 - cFocusFade * this.focusBlend) * this.nearFade)
		this.dome.opacity = 1 - 0.75 * this.focusBlend
	}

	/** Fade the marker down while focused so it doesn't obscure the storm structure */
	public setFocused(focused: boolean): void {
		if (this.focused === focused) {
			return
		}
		this.focused = focused

		this.focusAnim?.stop()
		this.focusAnim = new animation()
			.from(this.focusBlend)
			.to(focused ? 1 : 0)
			.duration(0.5)
			.easing(easing.quadraticInOut)
			.onUpdate(value => {
				this.focusBlend = value
				this.applyBlends()
			})
		this.focusAnim.start()
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.visible) {
			return []
		}

		const nowMs = performance.now()
		const dt = this.lastFrameMs > 0 ? Math.min((nowMs - this.lastFrameMs) / 1000, 0.1) : 1 / 60
		this.lastFrameMs = nowMs

		// Cyclonic rotation: counterclockwise in the northern hemisphere,
		// clockwise in the southern. In this tangent frame the up direction
		// is local -Y, so a NEGATIVE angle about +Y turns counterclockwise
		// as seen from above (looking down at the storm).
		const spinRate = cSpinBaseRadPerSec + cSpinPerCategory * Math.max(0, this.style.category)
		const direction = this.style.latitude >= 0 ? -1 : 1
		this.spinAngle += direction * spinRate * dt
		this.spinner.transform = matrix.axisRotation(vec3.unitY, this.spinAngle)

		// The symbol only earns its draw calls at regional zoom; from orbit
		// it would collapse to a few pixels and the dome carries the visual.
		// state.modelMatrix holds the parent feature's accumulated transform,
		// which is this marker's world placement.
		const distance = state.camera.position.subtract(state.modelMatrix.position).length

		// Dive toward the surface and the flat symbol fades away instead of
		// filling the view; far earlier when the real clouds are drawn. No
		// store means an embed, whose hurricane embeds always carry clouds
		const cloudsOn = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("cloud") ?? true
		const fadeStart = cloudsOn ? cCloudNearFadeStartKm : cNearFadeStartKm
		const fadeEnd = cloudsOn ? cCloudNearFadeEndKm : cNearFadeEndKm
		this.nearFade = Math.min(1, Math.max(0, (distance - fadeEnd) / (fadeStart - fadeEnd)))
		this.applyBlends()

		// The flat symbol renders between the far gate (too small to read)
		// and the near fade (too big to tolerate). A recorded clip drops the
		// marker entirely: the clouds show the storm, and a spinning symbol
		// or pulsing dome over the eye reads as UI, not weather
		const recording = isVideoRenderMode()
		this.spinner.visible = !recording
			&& cSymbolRadiusKm > distance * cSymbolMinAngular
			&& this.nearFade > 0.02
		this.dome.visible = !recording

		return super.render(state)
	}

	override destroy(): void {
		this.focusAnim?.stop()
		super.destroy()
	}
}
